import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { ENTER } from '@angular/cdk/keycodes';
import { FiDateFormatOptions, isDefined } from '@fi-shared/utils';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiFormHelperService } from '@fi-shared/fi-form-shared/fi-form-helper.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import {
    IFormAnswer,
    IFormQuestion,
    IFormViewer,
    IOpenFormViewerParams,
} from '@fi-shared/fi-form-shared/fi-form-shared';
import { FiFormSharedService } from '@fi-shared/fi-form-shared/fi-form-shared.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiFormConditionHelperService } from '@fi-shared/fi-form-shared/fi-form-condition-helper.service';
import {
    IContentLanguage,
    IContentLanguageLabelModel,
} from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { ILightWeightBusinessUnit } from '@fi-shared/models';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { PublicationItemStatus } from '@fi-shared/fi-edit-publication/publication-item-status';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';

@Component({
    selector: 'fi-form-viewer',
    templateUrl: './fi-form-viewer.component.html',
    styleUrls: ['./fi-form-viewer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-form-viewer' },
    standalone: false,
})
export class FiFormViewerComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() formViewer: IFormViewer;
    @Input() languageList: IContentLanguage[];
    @Input() selectedLanguageId: number;
    @Input() isMobileView: boolean = false;
    @Input() isTitleShown: boolean = true;
    @Input() previewMode = false;
    @Output() onSelectedLanguageIdChange = new EventEmitter<number>();
    @Output() onSave = new EventEmitter();
    @Output() showAnsweredForm = new EventEmitter<IOpenFormViewerParams>();
    @ViewChild('viewerForm', { static: false }) viewerFormRef: FiValidatingFormDirective;

    readonly _submitButtonId: string = 'submit-button';
    isFormValid: boolean = true;
    formAnswers: IFormAnswer[] = [];
    formAnswerBusinessUnitId: number;
    formDisabledText: string;
    isBusinessUnitSelectShown = false;

    constructor(
        private formSharedService: FiFormSharedService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private detectDeviceService: DetectDevicesService,
        private formHelperService: FiFormHelperService,
        private loadingService: LoadingService,
        private fiUserDataService: FiUserDataService,
        private renderer: Renderer2,
        private elementRef: ElementRef,
        private saveChangesWarningService: SaveChangesWarningService,
        private formConditionHelperService: FiFormConditionHelperService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    ngOnInit() {
        this.formAnswers = JSON.parse(JSON.stringify(this.formViewer.AnswerSet.Answers));
        this.renderer.listen(this.elementRef.nativeElement, 'keypress', ($event: KeyboardEvent) =>
            this.onKeyPress($event),
        );
        if (!this.previewMode) {
            this.saveChangesWarningService.start(() => this.formViewer);
        }

        const activeBusinessUnits = this.formViewer.FormBusinessUnits?.filter((bu) => bu.IsActivated) ?? []; // FormBusinessUnits can be null for preview
        this.formDisabledText = this.getFormDisabledText(activeBusinessUnits);

        if (!this.previewMode && !this.IsAnonymousAnswers() && !this.isFormDisabled()) {
            if (activeBusinessUnits.length === 0 && this.formViewer.UserSharedDirectly) {
                this.formAnswerBusinessUnitId = this.fiUserDataService.getUserBusinessUnitId();
            } else if (activeBusinessUnits.length === 1) {
                this.formAnswerBusinessUnitId = activeBusinessUnits[0].Id;
            } else if (activeBusinessUnits.length > 1) {
                this.isBusinessUnitSelectShown = true;
            }
        }
    }

    isSubmitButtonDisabled(): boolean {
        return (
            this.isFormDisabled() ||
            (!this.IsAnonymousAnswers() && !this.previewMode && !isDefined(this.formAnswerBusinessUnitId))
        );
    }

    isFormDisabled() {
        return !!this.formDisabledText;
    }

    private getFormDisabledText(activeBusinessUnits: ILightWeightBusinessUnit[]) {
        if (this.previewMode) return undefined;

        if (this.isViewAnswerAvailable()) {
            return this.appTextService.get('form.form_answered');
        }
        if (this.isFormWithAnswers()) {
            return this.appTextService
                .get('common.submission_date')
                .replace(
                    '<date>',
                    this.fiFormatDateService.formatDate(
                        new Date(this.formViewer.AnswerSet.ChangedOn),
                        FiDateFormatOptions,
                    ),
                );
        }
        if (this.formViewer.FormStatus !== PublicationItemStatus.Active) {
            return this.appTextService.get('form.form_not_shared');
        }
        if (this.formViewer.UserSharedDirectly) {
            return undefined;
        }
        if (!this.formViewer.HasViewAccess) {
            return this.appTextService.get('form.form_not_shared_with_you');
        }
        if (this.formViewer.IsDeleted) {
            return this.appTextService.get('form.form_disabled_deleted');
        }
        if (!this.IsAnonymousAnswers() && !activeBusinessUnits.length) {
            return this.appTextService.get('form.form_disabled_deactivated_unit');
        }
    }

    ngAfterViewInit() {
        if (this.formSharedService.isFormUnSeen(this.formViewer.Id)) {
            const _timer = timer(4000);
            _timer.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
                this.formSharedService.markFormAsVisited(this.formViewer.Id);
            });
        }
    }

    isPlainViewMode$(): Observable<boolean> {
        return this.formHelperService.plainViewMode$;
    }

    onKeyPress($event: KeyboardEvent) {
        if ($event.keyCode === ENTER) {
            if (($event.target as HTMLElement).tagName !== 'TEXTAREA') $event.preventDefault();

            if (($event.target as HTMLElement).id === this._submitButtonId) {
                this.viewerFormRef.submit();
            }
        }
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    selectionChange(event) {
        if (event.hasOwnProperty('value')) {
            this.onSelectedLanguageIdChange.emit(event.value);
        } else {
            this.onSelectedLanguageIdChange.emit(event);
        }
    }

    getPreferredContentLabelValue(items: IContentLanguageLabelModel[]) {
        if (items && items.length) {
            const preferredContentLabel = items.find((item) => item.ContentLanguageId === this.selectedLanguageId);
            return preferredContentLabel ? preferredContentLabel.Value : '';
        }
        return '';
    }

    getAnswerItem(idx: number) {
        return this.formViewer.AnswerSet.Answers[idx];
    }

    saveViewer() {
        this.isFormValid = true;
        this.loadingService.trackPromise(
            this.formSharedService.saveFormViewer(this.formViewer, this.formAnswerBusinessUnitId).then(() => {
                if (!this.previewMode) {
                    this.saveChangesWarningService.stop();
                }
                this.onSave.emit();
            }),
        );
    }

    onValidateError() {
        this.isFormValid = false;
        /*this.fiDialogsService.notify({
            messageText: this.appTextService.get('common.error_all_mandatory'),
            headerText: this.appTextService.get('common.warning'),
        });*/
    }

    isHiddenQuestion(question: IFormQuestion) {
        return this.formConditionHelperService.isQuestionHidden(
            question,
            this.formViewer.Questions,
            this.formViewer.Conditions,
            this.formViewer.AnswerSet.Answers,
        );
    }

    isNoTopMarginHeadingElement(question) {
        if (this.formHelperService.isHeadingElement(question) && !this.isHiddenQuestion(question)) {
            let firstHeadingElementIndex = this.formViewer.Questions.findIndex(
                (viewerQuestion) => viewerQuestion === question,
            );
            if (firstHeadingElementIndex !== -1 && firstHeadingElementIndex !== 0) {
                let questions = [...this.formViewer.Questions];
                let itemsBeforeHeadingElement = questions.splice(0, firstHeadingElementIndex);
                if (itemsBeforeHeadingElement.some((element) => !this.isHiddenQuestion(element))) {
                    return false;
                }
            }
        }
        return true;
    }

    isMobileScreenWide() {
        return this.detectDeviceService.isMobilePhone();
    }

    isMobileScreenSmall() {
        return this.detectDeviceService.isMobileSmall();
    }

    onBackClick() {
        window.history.back();
    }

    isViewAnswerAvailable() {
        return this.formViewer.IsCurrentUserAnswered && !this.formViewer.IsMultipleAnswer && !this.isFormWithAnswers();
    }

    isFormWithAnswers() {
        return !!this.formViewer.AnswerSet.Id;
        //return this.formAnswers.some(answer => answer.Value || answer.MediaBankFileReferences.length) || this.formAnswers.some(answer => answer.ValuePresetOptionIds.length);
    }

    goToAnsweredForm() {
        this.showAnsweredForm.emit({
            FormId: this.formViewer.Id,
            AnswerSetId: this.formViewer.LastCurrentUserAnswerSetId,
            FormName: this.formViewer.Name,
        });
    }

    IsAnonymousAnswers() {
        return this.formHelperService.isAnonymousAnswers(this.formViewer);
    }

    showDescriptionBlock() {
        return this.getPreferredContentLabelValue(this.formViewer.Descriptions) || this.IsAnonymousAnswers();
    }
}
